import classNames from 'classnames';
import * as React from 'react';
import type { InfoBannerBlockViewModel } from '~/Blocks/InfoBannerBlock/InfoBannerBlockViewModel.csharp';
import { useLogoSettings } from '~/Features/Configuration/Hooks/useLogoSettings';
import { NavigationVariant } from '~/Features/Navigation/NavigationVariant.csharp';
import type { NavigationViewModel } from '~/Features/Navigation/NavigationViewModel.csharp';
import { mapToMenuColumnsLayout } from '~/Partials/Header/Utils/menuColumnLayoutMapper';
import { InfoBannerWithState } from '~/Partials/InfoBanner/InfoBannerWithState';
import { KristianiaLogo } from '~/Partials/KristianiaLogo/KristianiaLogo';
import { Link } from '~/Partials/Link/Link';
import { LinkType } from '~/Partials/Link/LinkType.csharp';
import { LanguagePicker } from '~/Partials/Nav/Components/LanguagePicker/LanguagePicker';
import { resolveNavElementsColor } from '~/Partials/Nav/Components/Shared/Utils';
import { Nav } from '~/Partials/Nav/Nav';
import { useIsScrollOnTop } from '~/Utils/Hooks/useIsScrollOnTop';
import { useWindowWidthDetection } from '~/Utils/Hooks/useWindowWidthDetection';
import { MediaBreakpoints } from '~/Utils/MediaBreakpoints';
import { useScrollLockNotifier } from '~/Utils/ScrollLock/useScrollLockNotifier';
import styles from './Header.module.css';
import { useScrollDirection } from './Hooks/useScrollDirection';

export const hiddenHeaderClassName = 'PageWrapper--headerHidden';
export const infoBannerSettingKey = 'isInfoBannerClosed';

export interface HeaderProps {
    navigation: NavigationViewModel;
    infoBanner: InfoBannerBlockViewModel | null;
}

export const Header: React.FC<React.PropsWithChildren<HeaderProps>> = props => {
    const { navigation, infoBanner } = props;
    const { variant, translations, languages, startPageUrl } = navigation;

    const pageWrapperRef = React.useRef<HTMLElement>();
    const isMobile = useWindowWidthDetection({ condition: 'lessThan', breakpoint: MediaBreakpoints.Tablet });
    const lockState = useScrollLockNotifier();
    const { scrollDirection } = useScrollDirection(128, 'up', undefined, lockState.isLocked);

    const logoSettings = useLogoSettings();

    const isTransparent =
        variant === NavigationVariant.TransparentDark || variant === NavigationVariant.TransparentLight;

    React.useLayoutEffect(() => {
        pageWrapperRef.current = document.getElementById('PageWrapper') || undefined;
        updateMainClass();
    }, []);

    React.useEffect(() => {
        updateMainClass();
    }, [scrollDirection]);

    const updateMainClass = () => {
        if (scrollDirection === 'down' && isHeaderSticky && pageWrapperRef.current) {
            pageWrapperRef.current.classList.add(hiddenHeaderClassName);
        } else if (pageWrapperRef.current && pageWrapperRef.current.classList.contains(hiddenHeaderClassName)) {
            pageWrapperRef.current.classList.remove(hiddenHeaderClassName);
        }
    };

    const removeInfoBannerClassName = () => {
        pageWrapperRef.current?.classList.remove('InfoBanner--active');
    };

    const addInfoBannerClassName = () => {
        pageWrapperRef.current?.classList.add('InfoBanner--active');
    };
    const { isScrollOnTop } = useIsScrollOnTop();
    const [isSearchOpened, changeSearchOpened] = React.useState<boolean>(false);
    const isHeaderSticky = !isScrollOnTop;

    const getHeaderInlineStyle = () => {
        if (lockState.isLocked && !lockState.isScrollBarVisible) {
            return {
                paddingRight: `${lockState.scrollBarWidth}px`,
            };
        }

        return undefined;
    };

    const navElementsColor = resolveNavElementsColor(
        variant,
        lockState.isLocked,
        isHeaderSticky || false,
        isSearchOpened,
        isMobile || false
    );
    const shouldLogoBeWhite = navElementsColor === 'light';

    const firstOtherAvailableLanguage = languages.filter(el => el.url !== null)[1];

    return (
        <header
            className={classNames(
                styles.Header,
                isTransparent && [styles['Variant--transparent']],
                isHeaderSticky && styles['Variant--sticky'],
                isSearchOpened && styles['Variant--searchOpened'],
                lockState.isLocked && styles['Variant--scrollLocked']
            )}
            id="header"
            style={getHeaderInlineStyle()}
        >
            <a href="#mainContent" className={styles.SkipLink}>
                {translations.goToContent}
            </a>
            <InfoBannerWithState
                info={infoBanner}
                localStorageKey={infoBannerSettingKey}
                onOpened={addInfoBannerClassName}
                onClosed={removeInfoBannerClassName}
                closeTranslation={translations.closeInfoBanner}
            />
            {firstOtherAvailableLanguage && (
                <div className={styles.HeaderStickyLanguagePicker}>
                    <LanguagePicker
                        language={firstOtherAvailableLanguage}
                        variant={isScrollOnTop ? 'sticky' : 'stickyNarrow'}
                    />
                </div>
            )}
            <div className={styles.Wrapper}>
                <div className={styles.Inner}>
                    <Link type={LinkType.Clear} href={startPageUrl} className={styles.HomeLink}>
                        <KristianiaLogo
                            variant={logoSettings.headerVariant}
                            label={logoSettings.headerAltText || logoSettings.defaultAltText}
                            type="responsive"
                            color={shouldLogoBeWhite ? 'white' : 'black'}
                            size="large"
                        />
                    </Link>
                    <Nav
                        model={{
                            ...navigation,
                            menuColumnsLayout: mapToMenuColumnsLayout(navigation.menuColumnsCount),
                        }}
                        navElementsColor={navElementsColor}
                        changeSearchOpened={changeSearchOpened}
                    />
                </div>
            </div>
        </header>
    );
};
